import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { categoryData, defaultCategoryData } from '@/mocks/category';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? categoryData[slug] : null;
  const data = category || defaultCategoryData;
  const [activeSub, setActiveSub] = useState('全部');

  const filteredArticles = activeSub === '全部'
    ? data.articles
    : data.articles.filter((a) => a.subCategory === activeSub);

  useEffect(() => {
    document.title = `${data.name} - 黑料在线 - 最新网红黑料每日实时更新`;
  }, [data.name]);

  useEffect(() => {
    const baseUrl = window.location.origin + __BASE_PATH__;
    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${baseUrl}/category/${slug}`,
        name: `${data.name} - 黑料在线`,
        description: data.description,
        url: `${baseUrl}/category/${slug}`,
        isPartOf: { '@id': `${baseUrl}` }
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: `${baseUrl}/` },
          { '@type': 'ListItem', position: 2, name: data.name, item: `${baseUrl}/category/${slug}` }
        ]
      }
    ];
    const scripts: HTMLScriptElement[] = [];
    schemas.forEach((s) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(s);
      document.head.appendChild(script);
      scripts.push(script);
    });
    return () => { scripts.forEach((s) => document.head.removeChild(s)); };
  }, [data.name, slug]);

  return (
    <>
      <Navbar />
      <main>
        <section className="relative w-full min-h-[50vh] md:min-h-[60vh] flex items-center">
          <div className="absolute inset-0">
            <img
              src={data.heroImage}
              alt={data.name}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>
          </div>
          <div className="relative w-full max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-12">
            <div className="max-w-3xl">
              <nav className="flex items-center gap-2 text-xs text-white/60 mb-4" aria-label="面包屑导航">
                <Link to="/" className="hover:text-white transition-colors cursor-pointer">首页</Link>
                <i className="ri-arrow-right-s-line w-3.5 h-3.5 flex items-center justify-center"></i>
                <span className="text-white/90">{data.name}</span>
              </nav>
              <h1 className="text-3xl md:text-5xl font-heading font-black text-white leading-tight">
                {data.name}
              </h1>
              <p className="mt-4 text-sm md:text-base text-white/70 max-w-xl leading-relaxed">
                {data.description}
              </p>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-18 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-wrap items-center gap-2 mb-10">
              {data.subCategories.map((sub) => (
                <button
                  key={sub}
                  onClick={() => setActiveSub(sub)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${
                    activeSub === sub
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {sub}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group flex flex-col bg-background-100 rounded-xl overflow-hidden hover:bg-background-200/70 transition-colors cursor-pointer"
                >
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <span className="inline-block self-start px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-3 whitespace-nowrap">
                      {article.subCategory}
                    </span>
                    <h3 className="text-base md:text-lg font-semibold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors mb-auto">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-500 leading-relaxed mt-2 line-clamp-2">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-5 mt-4 text-xs text-foreground-400">
                      <span>{article.date}</span>
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line w-3.5 h-3.5 flex items-center justify-center"></i>
                        {article.views}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-chat-3-line w-3.5 h-3.5 flex items-center justify-center"></i>
                        {article.comments}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {filteredArticles.length === 0 && (
              <div className="text-center py-20">
                <p className="text-foreground-400 text-sm">该分类下暂无内容</p>
              </div>
            )}

            <div className="mt-10 flex items-center justify-center gap-3">
              <button className="w-10 h-10 rounded-full bg-primary-500 text-white flex items-center justify-center cursor-pointer text-sm font-semibold">1</button>
              <button className="w-10 h-10 rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 flex items-center justify-center cursor-pointer text-sm">2</button>
              <button className="w-10 h-10 rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 flex items-center justify-center cursor-pointer text-sm">3</button>
              <button className="w-10 h-10 rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 flex items-center justify-center cursor-pointer text-sm whitespace-nowrap">
                <i className="ri-arrow-right-s-line text-lg w-5 h-5 flex items-center justify-center"></i>
              </button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}